/**
 * Wires the Stripe Express Checkout Element wallet sheet to the same shipping pipeline PayPal uses.
 *
 * The endpoint we call (`/checkout/paypal/shipping/{token}`) is misleadingly named -- it lives in
 * the PayPal controller file but is fully wallet-agnostic: it just talks to `internal_api` and returns
 * the dealer's shipping methods + a fresh order total. Reusing it gives us 1:1 parity with PayPal
 * (same methods, same labels, same tax/handling math) at zero risk of drift.
 *
 * Response shape we map FROM (PayPal-ish):
 *   {
 *     shippingMethods: { methods: [{ id, deliveryTypeLabel, deliveryWindowLabel, deliveryType, amount, ... }] },
 *     order: { totalAmount, subtotalAmount, taxAmount, shippingAmount, handlingAmount, discountAmount, deliveryFeeAmount, shippingId }
 *   }
 *
 * Response shape we map TO (Stripe ECE event.resolve payload):
 *   {
 *     shippingRates: [{ id, displayName, amount }],     // amount in MINOR units (cents)
 *     lineItems:     [{ name,             amount }],    // amount in MINOR units (cents)
 *   }
 *
 * Stripe also requires the Elements amount to be updated before resolving, so callers receive a
 * `totalCents` to feed `elements.update({ amount })`.
 */
import debug from 'debug';
import get from 'lodash/get';
import { getWalletCurrencyCode, PICKUP_DELIVERY_TYPES } from './constants';
import request from './request';
import getOrCreateSessionUrls from './session';

const log = debug('rp:stripe-ece:shipping');

/**
 * Handle a `shippingaddresschange` event from Stripe ECE. Fetches the dealer's shipping methods for
 * the buyer's postal code + country, picks the cheapest non-pickup option, and returns the data the
 * caller needs to update Elements + resolve the event.
 *
 * @param {{ address: { postal_code?: string, country?: string } }} event
 * @returns {Promise<{ shippingRates: Array, lineItems: Array, totalCents: number } | null>}
 *   Returns `null` when the address is too partial to query (no rate change to apply yet).
 */
export async function handleAddressChange(event) {
	const postal_code = get(event, 'address.postal_code');
	const country = get(event, 'address.country');
	if (!postal_code || !country) {
		log('skipping address change: incomplete address', { postal_code, country });
		return null;
	}

	const { shippingUrl } = await getOrCreateSessionUrls();
	const response = await request(shippingUrl, {
		method: 'GET',
		query: { postalCode: postal_code, country },
	});

	return mapResponseToStripePayload(response);
}

/**
 * Handle a `shippingratechange` event from Stripe ECE. The shopper picked a rate; we persist it
 * server-side (so the eventual order completion uses the same method) and recompute the total.
 *
 * @param {{ shippingRate: { id: string, amount: number } }} event
 * @returns {Promise<{ shippingRates: Array, lineItems: Array, totalCents: number }>}
 */
export async function handleRateChange(event) {
	const shippingId = get(event, 'shippingRate.id');
	// Stripe ECE gives us amount in cents; the internal API persists it in dollars to match the
	// existing PayPal pipeline (`putShippingMethod`). Round-trip via toFixed(2) to avoid floats like
	// 12.939999... that would later fail strict comparisons.
	const cents = Number(get(event, 'shippingRate.amount', 0));
	const shippingAmount = (cents / 100).toFixed(2);

	const { shippingUrl } = await getOrCreateSessionUrls();
	const response = await request(shippingUrl, {
		method: 'GET',
		query: { shippingId, shippingAmount },
	});

	return mapResponseToStripePayload(response);
}

/**
 * Pure mapper from the PayPal-shaped shipping response to the payload Stripe ECE expects. Exported
 * for unit testing; production code reaches it via the handlers above.
 *
 * @param {object} response
 * @returns {{ shippingRates: Array<{id:string, displayName:string, amount:number}>, lineItems: Array<{name:string, amount:number}>, totalCents: number, currencyCode: string }}
 */
export function mapResponseToStripePayload(response) {
	const methods = get(response, 'shippingMethods.methods', []);
	const order = get(response, 'order', {});
	const selectedId = String(get(order, 'shippingId', '') || '');
	const currencyCode = getWalletCurrencyCode();

	const shippingRates = filterAndSortMethods(methods).map((method) => ({
		id: String(get(method, 'id', '')),
		displayName: buildDisplayName(method),
		amount: dollarsToCents(get(method, 'amount', 0)),
	}));

	// Move the buyer's selected rate (if any) to the top so Stripe highlights it; ECE auto-selects
	// the first option in the array when none is explicitly marked.
	if (selectedId) {
		const idx = shippingRates.findIndex((rate) => rate.id === selectedId);
		if (idx > 0) {
			const [selected] = shippingRates.splice(idx, 1);
			shippingRates.unshift(selected);
		}
	}

	const tax = dollarsToCents(get(order, 'taxAmount', 0));
	const shipping = dollarsToCents(get(order, 'shippingAmount', 0));
	const handling = dollarsToCents(get(order, 'handlingAmount', 0));
	const discount = dollarsToCents(get(order, 'discountAmount', 0));
	const deliveryFee = dollarsToCents(get(order, 'deliveryFeeAmount', 0));
	const total = dollarsToCents(get(order, 'totalAmount', 0));

	// Stripe ECE throws `IntegrationError: The amount N is less than the total amount of the line
	// items provided` whenever the Elements `amount` (our server-authoritative `total`) is below the
	// SUM of the line items we display. Each order component is rounded to cents independently, so
	// summing them can drift a cent or two ABOVE the independently-rounded `total` and trip that
	// guard. To stay exactly consistent, DERIVE the subtotal line as the residual rather than
	// trusting `subtotalAmount` directly. `total` is already net of any discount, so we add `discount`
	// back into the residual: this yields the gross (pre-discount) subtotal and the negative `Discount`
	// line below cancels it out, so the itemization always sums back to `total`.
	//
	// Stripe ECE supports negative-amount line items for discounts, so we surface the promotion as its
	// own `Discount` line for parity with PayPal's `discount` breakdown, instead of silently folding it
	// into the subtotal. Non-discount lines must be non-negative; we also drop $0 lines below because
	// some sheet variants reject those.
	const addOns = shipping + tax + handling + deliveryFee;
	const subtotal = Math.max(0, total - addOns + discount);

	const candidateLineItems = [
		{ amount: subtotal, name: 'Subtotal' },
		{ amount: -discount, name: 'Discount' },
		{ amount: shipping, name: 'Shipping' },
		{ amount: tax, name: 'Tax' },
		{ amount: handling, name: 'Handling' },
		{ amount: deliveryFee, name: 'Delivery fee' },
	];
	const lineItems = candidateLineItems.filter((item) => Number.isInteger(item.amount) && item.amount !== 0);

	const payload = {
		currencyCode,
		lineItems,
		shippingRates,
		totalCents: total,
	};

	// Cheap, gated debug log -- only writes when `localStorage.debug` includes our namespace, so it's
	// safe to leave in production. Lets us verify the resolve payload without redeploying.
	log('mapped wallet payload: %o', payload);

	return payload;
}

/**
 * @param {Array} methods
 * @returns {Array}
 */
function filterAndSortMethods(methods) {
	if (!Array.isArray(methods)) {
		return [];
	}
	// Drop pickup options -- a digital wallet has no UI to collect a pickup location.
	return methods
		.filter((method) => !PICKUP_DELIVERY_TYPES.includes(String(get(method, 'deliveryType', ''))))
		.sort((a, b) => Number(get(a, 'amount', 0)) - Number(get(b, 'amount', 0)));
}

/**
 * @param {object} method
 * @returns {string}
 */
function buildDisplayName(method) {
	const label = String(get(method, 'deliveryTypeLabel', '') || 'Shipping');
	const window = String(get(method, 'deliveryWindowLabel', '') || '');
	return window ? `${label} (Est. ${window})` : label;
}

/**
 * Converts a dollar string/number to cents, rounding to the nearest integer.
 *
 * Internal-API amounts come back as numbers in some endpoints and dollar strings ("12.94") in others.
 * Anything we can't parse becomes 0 rather than NaN so a single bad field doesn't blow up the total.
 *
 * @param {number | string} value
 * @returns {number}
 */
function dollarsToCents(value) {
	const dollars = Number(value);
	if (!Number.isFinite(dollars)) {
		return 0;
	}
	return Math.round(dollars * 100);
}
